import { useContext, useEffect, useState } from "react";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import GlobalContext from "src/context/global-context";
import { IKwickpayTransaction, IMTSTable } from "src/lib/constants/table-type";
import { ConversionTransaction, IMTS, PaginationInput } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import client from "../../api";
import { useMutation } from "react-query";
import { capitalizeFirstLetter, getErrorMessage, logger } from "src/lib/utils";
import { ColumnsType } from "antd/es/table";
import { Table } from "antd";
import { ECDH } from "crypto";

const ConversionListPage = ({
  searchText,
  onSelectedInvoiceData,
  onSelectedUser,
  searchField,
  startDate,
  endDate,
  isBuisiness,
  timezone,
}: {
  searchText: string;
  onSelectedInvoiceData: any;
  onSelectedUser: any;
  searchField: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isBuisiness?: boolean;
  timezone: string;
}) => {
  const navigate = useNavigate();

  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const [dataSource, setDataSource] = useState<ConversionTransaction[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  logger.log(searchField, " ", searchText, "DEEE###");
  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText]);

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getList, isLoading } = useMutation(
    isBuisiness
      ? client.conversions.getOrderList
      : client.conversions.getOrderList,
    {
      onSuccess: (data: any) => {
        const dataObj = data.data;
        setTotal(dataObj.total);
        setDataSource(
          dataObj?.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            reference_id: item.reference_id,
            user_id: item.user_id,
            from_currency: item.from_currency,
            to_currency: item.to_currency,
            amount: item.amount,
            converted_amount: item.converted_amount,
            rate_used: item.rate_used,
            mts_status: item.mts_status,
            status: item?.status,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            fullName: `${item.user.user_data.first_name || "_"} ${
              item.user.user_data.last_name || ""
            }`.trim(),
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText?.length > 0 ? searchText : undefined,
      searchField: searchField?.length > 0 ? searchField : undefined,
      type: isBuisiness ? "business" : "individual",
      timezone: timezone,
    });
  }, [isMounted, canViewTransactions, startDate, endDate, searchParams.current_page, searchField]);

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      current_page: 1,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText.length > 0 ? searchText : undefined,
      searchField: searchField?.length > 0 ? searchField : undefined,
      type: isBuisiness ? "business" : "individual",
      timezone: timezone,
    });
  }, [searchText, canViewTransactions]);

  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    logger.log("RECORD", record);
    setDownloadingId(record.id);
    downloadReceipt(
      {
        type: "conversion",
        id: record.id,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      },
    );
  };

  const columns: ColumnsType<ConversionTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.time")} */}
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Id
        </p>
      ),
      dataIndex: "user_id",
      className: "w-auto",
      key: "user_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full Name
        </p>
      ),
      dataIndex: "fullName",
      className: "w-auto",
      key: "fullName",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.service")} */}
          Date
        </p>
      ),
      dataIndex: "createdAt",
      className: "w-auto",
      key: "createdAt",
      render: (value, record: ConversionTransaction) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {moment(value).local().format("YYYY-MM-DD HH:mm:ss")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.amount")} */}
          Currency
        </p>
      ),
      dataIndex: "to_currency",
      className: "w-auto",
      key: "to_currency",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {variables?.from_currency?.toUpperCase()}/{value.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.amount")} */}
          Sell
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.fee")} */}
          Buy
        </p>
      ),
      dataIndex: "converted_amount",
      className: "w-auto",
      key: "converted_amount",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.status")} */}
          Exchange Rate
        </p>
      ),
      dataIndex: "rate_used",
      className: "w-auto",
      key: "rate_used",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {getPaymentStatusName(fetchStatus(value).toLowerCase())} */}
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.status")} */}
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {getPaymentStatusName(fetchStatus(value).toLowerCase())} */}
            {value?.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receipt
        </p>
      ),
      dataIndex: "receipt",
      className: "w-auto",
      key: "receipt",
      render: (_, record) =>
        record.mts_status?.toLowerCase() !== "ok" || !canDownload ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
        ) : downloadingId === Number(record.id) ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">
            Downloading...
          </p>
        ) : (
          <a
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
          >
            Receipt
          </a>
        ),
    },
  ];
  // const mobileColumns: ColumnsType<IMTSTable> = [
  //     {
  //         title: () => (
  //             <p className="font-manrope text-secondary text-[14px] font-medium">
  //                 MTS
  //             </p>
  //         ),
  //         dataIndex: "action",
  //         className: "w-auto",
  //         render: (_, record: { key: React.Key }) => {
  //             const selData = dataSource.filter((item) => item.key === record.key)[0];

  //             return (
  //                 <div className="w-full flex flex-row gap-2 justify-between">
  //                     <div className="w-full flex flex-col items-start gap-2">
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 ID
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.id}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Request ID
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.request_id || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Channel
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.channel || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Name
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_full_name || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Currency
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData &&
  //                                     capitalizeFirstLetter(
  //                                         selData.sender_currency.replaceAll("_", " ")
  //                                     )}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender DOB
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_dob}
  //                             </p>
  //                         </div>

  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Personal Number
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_personal_number}
  //                             </p>
  //                         </div>

  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Currency
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_currency}
  //                             </p>
  //                         </div>
  //                     </div>

  //                     <div className="w-full flex flex-col items-start gap-2">
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Amount
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_amount || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Bank Code
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_bank_code || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Sender Bank Name
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.sender_bank_name || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Receiver Name
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.receiver_full_name || "---"}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Receiver DOB
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.receiver_dob || "---"}
  //                             </p>
  //                         </div>

  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Receiver Personal Number
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.receiver_personal_number || "---"}
  //                             </p>
  //                         </div>

  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Receiver Currency
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.receiver_currency &&
  //                                     capitalizeFirstLetter(
  //                                         selData.receiver_currency.replaceAll("_", " ")
  //                                     )}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Receiver Iban
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {selData.receiver_iban &&
  //                                     capitalizeFirstLetter(
  //                                         selData.receiver_iban.replaceAll("_", " ")
  //                                     )}
  //                             </p>
  //                         </div>

  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Created
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
  //                                 {moment
  //                                     .utc(selData.created_at)
  //                                     .utcOffset(240)
  //                                     .format("DD/MM/YYYY H:mm")}
  //                             </p>
  //                         </div>
  //                         <div className="py-[4px]">
  //                             <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
  //                                 Updated
  //                             </p>
  //                             <p className="text-primary text-[13px] font-manrope font-medium">
  //                                 {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
  //                                 {moment
  //                                     .utc(selData.updated_at)
  //                                     .utcOffset(240)
  //                                     .format("DD/MM/YYYY H:mm")}
  //                             </p>
  //                         </div>
  //                     </div>
  //                 </div>
  //             );
  //         },
  //     },
  // ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? columns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default ConversionListPage;
